/**
 * Group chat hooks: the chat list, an open chat's messages (polled every few seconds while visible,
 * `?since=` deltas merged by id), sending through the durable outbox (offline: the message stays in the
 * chat marked "queued" and is re-sent in order when the network is back), edit / delete (online only),
 * read markers and muting. Threads are persisted, so a chat can be read offline.
 *
 * Query keys: ['chats'] (list), ['chat-messages', chatId] (ChatThread).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ChatId } from '../../shared/chats';
import type { AuthorDTO, ChatMessageDTO, ChatMessagesDTO, ChatSummaryDTO, CreateChatMessageRequest } from '../../shared/dto/chat';
import { byCreated, mergeMessages, oldestServerMessage, resetMessages, serverWatermark, type ChatItem, type ChatThread } from '../chat/logic';
import { enqueue, registerOutboxHandler, removeQueued, type EnqueueOutcome } from '../sync/outbox';
import { api, serverNow } from './client';
import { dropNotification } from './notifications';
import { visibleInterval } from './poll';
import { queryClient as rootQueryClient } from './queryClient';

export const CHATS_KEY = ['chats'] as const;
export const chatKey = (chatId: ChatId) => ['chat-messages', chatId] as const;

/** An open chat polls this often while visible and the conversation is live (a message in the last 2 min)… */
export const CHAT_POLL_MS = 4_000;
/** …and this often when it is quiet. Nothing at all in the background. */
export const CHAT_POLL_QUIET_MS = 12_000;
const LIVE_MS = 2 * 60_000;

/** Poll interval for an open chat (pure; `now` = estimated server time). */
export function chatPollInterval(thread: ChatThread | undefined, now: number, visible: boolean): number | false {
  if (!visible) return false;
  const newest = thread?.messages.at(-1)?.createdAt ?? 0;
  return now - newest < LIVE_MS ? CHAT_POLL_MS : CHAT_POLL_QUIET_MS;
}

const chatPath = (chatId: ChatId) => `/chats/${encodeURIComponent(chatId)}`;

/** Edits / deletes of mine on their way: a polled row older than my optimistic copy must not undo them. */
const inFlight = new Set<string>();

/** Drops cached threads of chats I may no longer read (another voice now, or the role changed). */
function pruneThreads(qc: QueryClient, list: readonly ChatSummaryDTO[]): void {
  const ids = new Set<string>(list.map((c) => c.id));
  for (const q of qc.getQueryCache().findAll({ queryKey: ['chat-messages'] })) {
    const id = q.queryKey[1];
    if (typeof id === 'string' && !ids.has(id)) qc.removeQueries({ queryKey: q.queryKey, exact: true });
  }
}

/**
 * The chats I may read. The list screen refetches on every visit and polls every 30 s while on screen;
 * a chat only needs its own entry (mute state) and passes `{ poll: false, staleTime }`.
 */
export function useChats({ poll = true, staleTime = 0 }: { poll?: boolean; staleTime?: number } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: CHATS_KEY,
    queryFn: async () => {
      const list = await api<ChatSummaryDTO[]>('/chats');
      pruneThreads(qc, list);
      return list;
    },
    meta: { persist: true },
    staleTime,
    refetchInterval: poll ? visibleInterval(30_000) : false,
    refetchIntervalInBackground: false,
  });
}

function setThread(qc: QueryClient, chatId: ChatId, fn: (t: ChatThread) => ChatThread): void {
  qc.setQueryData<ChatThread>(chatKey(chatId), (t) => fn(t ?? { messages: [], hasOlder: false, unreadAfter: 0 }));
}

function setMessage(qc: QueryClient, chatId: ChatId, id: string, fn: (m: ChatItem) => ChatItem | null): void {
  qc.setQueryData<ChatThread>(chatKey(chatId), (t) => {
    if (!t) return t;
    const out: ChatItem[] = [];
    for (const m of t.messages) {
      if (m.id !== id) out.push(m);
      else {
        const next = fn(m);
        if (next) out.push(next);
      }
    }
    return { ...t, messages: out };
  });
}

/**
 * Messages of one chat: the newest page first, then `?since=` deltas (5 s overlap) every 4 s while the
 * conversation is live, 12 s while it is quiet — only while the chat is on screen and the page is visible.
 */
export function useChatMessages(chatId: ChatId) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: chatKey(chatId),
    queryFn: async (): Promise<ChatThread> => {
      const prev = qc.getQueryData<ChatThread>(chatKey(chatId));
      const mark = prev ? serverWatermark(prev.messages) : null;
      const qs = mark === null ? '' : `?since=${Math.max(0, mark - 5000)}`;
      const res = await api<ChatMessagesDTO>(`${chatPath(chatId)}/messages${qs}`);
      // Re-read: optimistic changes may have happened while the request was in flight.
      const cur = qc.getQueryData<ChatThread>(chatKey(chatId));
      const list = cur?.messages ?? [];
      if (res.reset) return { messages: resetMessages(list, res.messages, inFlight), hasOlder: res.hasOlder, unreadAfter: res.unreadAfter };
      return { messages: mergeMessages(list, res.messages, inFlight), hasOlder: cur?.hasOlder ?? false, unreadAfter: Math.max(cur?.unreadAfter ?? 0, res.unreadAfter) };
    },
    meta: { persist: true },
    staleTime: 2_000,
    refetchInterval: (query) => chatPollInterval(query.state.data, serverNow(), typeof document !== 'undefined' && document.visibilityState === 'visible'),
    refetchIntervalInBackground: false,
  });
}

/** "Sýna eldri skilaboð": the page before the oldest message I have. */
export async function loadOlderMessages(qc: QueryClient, chatId: ChatId): Promise<void> {
  const cur = qc.getQueryData<ChatThread>(chatKey(chatId));
  const oldest = cur ? oldestServerMessage(cur.messages) : null;
  if (!oldest) return;
  const res = await api<ChatMessagesDTO>(`${chatPath(chatId)}/messages?before=${oldest.createdAt}&beforeId=${encodeURIComponent(oldest.id)}`);
  setThread(qc, chatId, (t) => ({ ...t, messages: mergeMessages(t.messages, res.messages, inFlight), hasOlder: res.hasOlder }));
}

/* ─── Sending (durable outbox) ──────────────────────────────────────────── */

/** Outbox body of a new message: the request plus what the chat needs to show it after a restart. */
interface QueuedChatMessage {
  chatId: ChatId;
  req: CreateChatMessageRequest;
  author: AuthorDTO;
  replyTo: ChatMessageDTO['replyTo'];
  createdAt: number;
}

/** The QueryClient the outbox handler writes to (the app's; tests pass their own to submit). */
let outboxQc: QueryClient = rootQueryClient;

/** Messages still queued after a restart, put back into their chat (marked "queued") when it loads. */
const restored = new Map<string, ChatItem>();
let watching = false;

function injectRestored(qc: QueryClient, chatId: string): void {
  const t = qc.getQueryData<ChatThread>(['chat-messages', chatId]);
  if (!t) return;
  const mine = [...restored.values()].filter((m) => m.chatId === chatId);
  if (mine.length === 0) return;
  const byId = new Map(t.messages.map((m) => [m.id, m]));
  let changed = false;
  for (const m of mine) {
    const cur = byId.get(m.id);
    if (!cur || cur.local === 'sending') {
      byId.set(m.id, m);
      changed = true;
    }
  }
  if (changed) qc.setQueryData<ChatThread>(['chat-messages', chatId], { ...t, messages: [...byId.values()].sort(byCreated) });
}

function watchThreads(qc: QueryClient): void {
  if (watching) return;
  watching = true;
  qc.getQueryCache().subscribe((ev) => {
    if (restored.size === 0 || ev.type !== 'updated' || ev.action.type !== 'success') return;
    const [root, chatId] = ev.query.queryKey;
    if (root === 'chat-messages' && typeof chatId === 'string') injectRestored(qc, chatId);
  });
}

function acceptServerMessage(qc: QueryClient, chatId: ChatId, id: string, res: unknown): void {
  if (res && typeof res === 'object' && (res as ChatMessageDTO).id === id && 'author' in res) setMessage(qc, chatId, id, () => ({ ...(res as ChatMessageDTO) }));
  else setMessage(qc, chatId, id, (m) => ({ ...m, local: undefined }));
}

type RejectionListener = (chatId: ChatId, error: unknown) => void;
const rejectionListeners: RejectionListener[] = [];

/** A message queued offline was later refused by the server (it stays in the chat marked "Ekki sent"). */
export function onChatMessageRejected(fn: RejectionListener): () => void {
  rejectionListeners.push(fn);
  return () => {
    const i = rejectionListeners.lastIndexOf(fn);
    if (i >= 0) rejectionListeners.splice(i, 1);
  };
}

registerOutboxHandler('chat', {
  prepare: (item) => (item.body as QueuedChatMessage).req,
  onSent(item, res) {
    const { chatId, req } = item.body as QueuedChatMessage;
    restored.delete(req.id);
    acceptServerMessage(outboxQc, chatId, req.id, res);
  },
  onRejected(item, error, immediate) {
    const { chatId, req } = item.body as QueuedChatMessage;
    restored.delete(req.id);
    setMessage(outboxQc, chatId, req.id, (m) => ({ ...m, local: 'failed' }));
    if (!immediate) rejectionListeners[rejectionListeners.length - 1]?.(chatId, error);
  },
  onRestore(item) {
    const { chatId, req, author, replyTo, createdAt } = item.body as QueuedChatMessage;
    restored.set(req.id, {
      id: req.id,
      chatId,
      author,
      replyTo,
      body: req.body,
      createdAt,
      updatedAt: createdAt,
      editedAt: null,
      deletedAt: null,
      local: 'queued',
    });
    watchThreads(outboxQc);
    injectRestored(outboxQc, chatId);
  },
  onReset() {
    restored.clear();
    inFlight.clear();
  },
});

export type SendResult = 'sent' | 'queued';

function sendResult(o: EnqueueOutcome, onQueued: () => void): SendResult {
  if (o.status === 'sent') return 'sent';
  if (o.status === 'queued') {
    onQueued();
    return 'queued';
  }
  throw o.error;
}

/**
 * Sends a message with a client UUID (idempotent on retry). It shows at once; offline it stays marked
 * "queued" and goes out when the network returns. A first attempt the server refuses is rethrown.
 */
export async function sendChatMessage(
  chatId: ChatId,
  req: CreateChatMessageRequest,
  author: AuthorDTO,
  replyTo: ChatMessageDTO['replyTo'],
  qc: QueryClient = rootQueryClient,
): Promise<SendResult> {
  outboxQc = qc;
  const now = serverNow();
  const existing = qc.getQueryData<ChatThread>(chatKey(chatId))?.messages.find((m) => m.id === req.id);
  if (existing) setMessage(qc, chatId, req.id, (m) => ({ ...m, local: 'sending' }));
  else {
    const optimistic: ChatItem = { id: req.id, chatId, author, replyTo, body: req.body, createdAt: now, updatedAt: now, editedAt: null, deletedAt: null, local: 'sending' };
    setThread(qc, chatId, (t) => ({ ...t, messages: [...t.messages, optimistic].sort(byCreated) }));
  }
  const body: QueuedChatMessage = { chatId, req, author, replyTo, createdAt: existing?.createdAt ?? now };
  const outcome = await enqueue({ kind: 'chat', key: `chat:${req.id}`, method: 'POST', path: `${chatPath(chatId)}/messages`, body });
  // 'rejected': the handler already marked the message as failed.
  return sendResult(outcome, () => setMessage(qc, chatId, req.id, (m) => ({ ...m, local: 'queued' })));
}

/** Removes a message the server never accepted (queued or failed). */
export function discardLocalChatMessage(chatId: ChatId, id: string, qc: QueryClient = rootQueryClient): void {
  restored.delete(id);
  void removeQueued(`chat:${id}`).catch(() => undefined);
  setMessage(qc, chatId, id, () => null);
}

/* ─── Edit / delete (online) ────────────────────────────────────────────── */

async function optimistic(qc: QueryClient, chatId: ChatId, id: string, change: (m: ChatItem) => ChatItem, send: () => Promise<unknown>): Promise<void> {
  const before = qc.getQueryData<ChatThread>(chatKey(chatId))?.messages.find((m) => m.id === id);
  inFlight.add(id);
  setMessage(qc, chatId, id, change);
  try {
    const res = await send();
    inFlight.delete(id);
    if (res && typeof res === 'object' && (res as ChatMessageDTO).deletedAt) setMessage(qc, chatId, id, () => null);
    else acceptServerMessage(qc, chatId, id, res);
  } catch (e) {
    inFlight.delete(id);
    if (before) setMessage(qc, chatId, id, () => before);
    throw e;
  }
}

export function useEditChatMessage(chatId: ChatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => {
      const now = serverNow();
      return optimistic(
        qc,
        chatId,
        id,
        (m) => ({ ...m, body, editedAt: now, updatedAt: now }),
        () => api<unknown>(`/chat-messages/${encodeURIComponent(id)}`, { method: 'PATCH', body: { body } }),
      );
    },
  });
}

export function useDeleteChatMessage(chatId: ChatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      const now = serverNow();
      return optimistic(
        qc,
        chatId,
        id,
        (m) => ({ ...m, body: null, deletedAt: now, updatedAt: now }),
        () => api<unknown>(`/chat-messages/${encodeURIComponent(id)}`, { method: 'DELETE' }),
      ).then(() => void qc.invalidateQueries({ queryKey: CHATS_KEY }));
    },
  });
}

/* ─── Read markers & muting ─────────────────────────────────────────────── */

/**
 * Marks the chat read up to `upTo` (createdAt of the newest message I was shown). The list and the bell
 * update at once; best effort (the next visit marks it again).
 */
export async function markChatRead(chatId: ChatId, upTo: number, qc: QueryClient = rootQueryClient): Promise<void> {
  try {
    await api<void>(`${chatPath(chatId)}/read`, { method: 'POST', body: { upTo } });
  } catch {
    return;
  }
  qc.setQueryData<ChatSummaryDTO[]>(CHATS_KEY, (list) =>
    list?.map((c) => (c.id === chatId && upTo > c.lastReadAt ? { ...c, lastReadAt: upTo, unread: c.last && c.last.createdAt > upTo ? c.unread : 0 } : c)),
  );
  setThread(qc, chatId, (t) => ({ ...t, unreadAfter: Math.max(t.unreadAfter, upTo) }));
  dropNotification(qc, 'chat', chatId, upTo);
}

/** Notifications for a chat on / off (the bell and app badge; the chat list still counts unread). */
export function useMuteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, muted }: { chatId: ChatId; muted: boolean }) => api<ChatSummaryDTO>(`${chatPath(chatId)}/mute`, { method: 'PUT', body: { muted } }),
    onSuccess: (res) => {
      qc.setQueryData<ChatSummaryDTO[]>(CHATS_KEY, (list) => list?.map((c) => (c.id === res.id ? res : c)));
      if (res.muted) dropNotification(qc, 'chat', res.id);
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
